import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Upload, Image as ImageIcon, Trash2, FolderOpen, Plus, Edit2, FolderPlus } from 'lucide-react';
import Sidebar from '../components/Sidebar';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function ImageLibrary({ user }) {
  const [images, setImages] = useState([]);
  const [packs, setPacks] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [selectedPack, setSelectedPack] = useState('All');
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [quickPack, setQuickPack] = useState('');
  const [showPackDialog, setShowPackDialog] = useState(false);
  const [packForm, setPackForm] = useState({ name: '', description: '' });
  const [editingPack, setEditingPack] = useState(null);

  useEffect(() => {
    fetchImages();
    fetchPacks();
  }, []);

  useEffect(() => {
    if (selectedPack === 'All') {
      setFilteredImages(images);
    } else {
      setFilteredImages(images.filter(img => img.pack === selectedPack));
    }
  }, [selectedPack, images]);

  const fetchPacks = async () => {
    try {
      const response = await axios.get(`${API}/api/image-packs`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPacks(response.data);
      // Set first pack as default if available
      if (response.data.length > 0 && !quickPack) {
        setQuickPack(response.data[0].name);
      }
    } catch (error) {
      console.error('Error fetching packs:', error);
      toast.error('Failed to fetch packs');
    }
  };

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/api/image-library`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      console.log('📚 Fetched images:', response.data);
      setImages(response.data);
    } catch (error) {
      console.error('Error fetching images:', error);
      toast.error('Failed to fetch images');
    }
  };

  const handleQuickUpload = async (files) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    setUploadingFiles(prev => [...prev, ...fileArray.map(f => f.name)]);

    for (const file of fileArray) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', file.name.split('.')[0]); // Use filename as name
        formData.append('pack', quickPack);
        formData.append('modalities', JSON.stringify([]));
        formData.append('description', '');
        formData.append('tags', JSON.stringify([]));

        await axios.post(`${API}/api/image-library`, formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        toast.success(`✅ ${file.name} added!`);
      } catch (error) {
        console.error('Error uploading:', error);
        toast.error(`Failed: ${file.name}`);
      }
    }

    setUploadingFiles([]);
    fetchImages();
  };

  const handleDelete = async (imageId, imageUrl) => {
    if (!window.confirm('Delete this image from library?')) return;

    try {
      await axios.delete(`${API}/api/image-library/${imageId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Image deleted');
      fetchImages();
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Failed to delete image');
    }
  };

  const handleCreatePack = async () => {
    if (!packForm.name.trim()) {
      toast.error('Pack name is required');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('name', packForm.name.trim());
      if (packForm.description) formData.append('description', packForm.description);

      await axios.post(`${API}/api/image-packs`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success(`✅ Pack "${packForm.name}" created!`);
      setPackForm({ name: '', description: '' });
      setShowPackDialog(false);
      fetchPacks();
    } catch (error) {
      console.error('Error creating pack:', error);
      toast.error(error.response?.data?.detail || 'Failed to create pack');
    }
  };

  const handleEditPack = async () => {
    if (!packForm.name.trim()) {
      toast.error('Pack name is required');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('name', packForm.name.trim());
      if (packForm.description) formData.append('description', packForm.description);

      await axios.put(`${API}/api/image-packs/${editingPack.id}`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success(`✅ Pack "${packForm.name}" updated!`);
      setPackForm({ name: '', description: '' });
      setEditingPack(null);
      setShowPackDialog(false);
      fetchPacks();
      fetchImages(); // Refresh images if pack name changed
    } catch (error) {
      console.error('Error editing pack:', error);
      toast.error(error.response?.data?.detail || 'Failed to edit pack');
    }
  };

  const handleDeletePack = async (pack) => {
    if (pack.is_default) {
      toast.error('Cannot delete default packs');
      return;
    }

    if (!window.confirm(`Delete pack "${pack.name}"? This will only work if the pack has no images.`)) return;

    try {
      await axios.delete(`${API}/api/image-packs/${pack.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success(`Pack "${pack.name}" deleted`);
      fetchPacks();
    } catch (error) {
      console.error('Error deleting pack:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete pack');
    }
  };

  const openEditPackDialog = (pack) => {
    setEditingPack(pack);
    setPackForm({ name: pack.name, description: pack.description || '' });
    setShowPackDialog(true);
  };

  const openCreatePackDialog = () => {
    setEditingPack(null);
    setPackForm({ name: '', description: '' });
    setShowPackDialog(true);
  };


  return (
    <div className="min-h-screen">
      <Sidebar user={user} />
      <div className="main-content max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">📚 Image Library</h1>
        <p className="text-gray-600">Quick-add reference images by pack</p>
      </div>

      {/* Quick Upload Area - Tag-like */}
      <Card className="p-4 mb-6 bg-gradient-to-r from-blue-50 to-emerald-50">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Label className="text-sm font-medium mb-2 block">Quick Add Images</Label>
            <div className="flex gap-2">
              <select
                value={quickPack}
                onChange={(e) => setQuickPack(e.target.value)}
                className="border rounded px-3 py-2 text-sm bg-white"
              >
                {packs.map(pack => (
                  <option key={pack.id} value={pack.name}>{pack.name}</option>
                ))}
              </select>
              <label className="flex-1 cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleQuickUpload(e.target.files)}
                  className="hidden"
                />
                <div className="border-2 border-dashed border-blue-300 rounded px-4 py-2 text-center bg-white hover:bg-blue-50 transition-colors">
                  <Upload className="w-4 h-4 inline mr-2" />
                  <span className="text-sm font-medium">Click to upload or drag files</span>
                </div>
              </label>
            </div>
            {uploadingFiles.length > 0 && (
              <p className="text-xs text-gray-600 mt-2">Uploading: {uploadingFiles.join(', ')}</p>
            )}
          </div>
        </div>
      </Card>

      {/* Pack Management */}
      <Card className="p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FolderPlus className="w-5 h-5 text-gray-600" />
            <Label className="font-semibold">Manage Packs:</Label>
          </div>
          <Button size="sm" onClick={openCreatePackDialog}>
            <Plus className="w-4 h-4 mr-1" />
            New Pack
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {packs.map(pack => (
            <div key={pack.id} className="group relative">
              <Button
                variant="outline"
                size="sm"
                className="pr-8"
              >
                {pack.name} ({pack.image_count || 0})
              </Button>
              <div className="absolute right-1 top-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                {!pack.is_default && (
                  <>
                    <button
                      onClick={() => openEditPackDialog(pack)}
                      className="p-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      <Edit2 className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleDeletePack(pack)}
                      className="p-1 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Pack Filter */}
      <Card className="p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <FolderOpen className="w-5 h-5 text-gray-600" />
          <Label className="font-semibold">Filter by Pack:</Label>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={selectedPack === 'All' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedPack('All')}
          >
            All ({images.length})
          </Button>
          {packs.filter(pack => pack.image_count > 0).map(pack => (
            <Button
              key={pack.id}
              variant={selectedPack === pack.name ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedPack(pack.name)}
            >
              {pack.name} ({pack.image_count})
            </Button>
          ))}
        </div>
      </Card>

      {/* Images Grid */}
      {console.log('🖼️ Rendering grid with:', filteredImages.length, 'images')}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {filteredImages.map(image => {
          // Handle both full URLs and relative paths
          const imageSource = image.image_url.startsWith('http') ? image.image_url : `${API}${image.image_url}`;
          console.log(`Image Library: ${image.name} -> ${image.image_url} -> ${imageSource}`);
          
          return (
          <Card key={image.id} className="overflow-hidden group">
            <div className="relative aspect-square">
              <img
                src={imageSource}
                alt={image.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  console.error('❌ Image failed to load:', imageSource);
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EError%3C/text%3E%3C/svg%3E';
                }}
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDelete(image.id, image.image_url)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="p-2">
              <h3 className="font-medium text-xs truncate">{image.name}</h3>
              <p className="text-xs text-gray-600">{image.pack}</p>
            </div>
          </Card>
          );
        })}
      </div>

      {filteredImages.length === 0 && (
        <Card className="p-12 text-center">
          <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">
            {selectedPack === 'All' ? 'No images yet' : `No images in ${selectedPack} pack`}
          </h3>
          <p className="text-gray-500 mb-4">Select a pack and drag images to the upload area above</p>
        </Card>
      )}

      {/* Pack Management Dialog */}
      <Dialog open={showPackDialog} onOpenChange={setShowPackDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPack ? 'Edit Pack' : 'Create New Pack'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Pack Name *</Label>
              <Input
                value={packForm.name}
                onChange={(e) => setPackForm({ ...packForm, name: e.target.value })}
                placeholder="e.g., Mountain Biking, Trail Running"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={packForm.description}
                onChange={(e) => setPackForm({ ...packForm, description: e.target.value })}
                placeholder="Optional description..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowPackDialog(false)}>
                Cancel
              </Button>
              <Button onClick={editingPack ? handleEditPack : handleCreatePack}>
                {editingPack ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
